import { useState } from 'react'
import { Plus, Pencil } from 'lucide-react'
import { IconButton } from '@doculegis/shared'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ContractTypeSheet,
  type ContractTypeSheetDraft,
} from '@/components/contract-types/ContractTypeSheet'
import { usePermission } from '@/contexts/AuthContext'
import { useContractTypes } from '@/hooks/useSupabase'

interface ContractTypeSelectProps {
  value: string
  onChange: (value: string) => void
  hideLabel?: boolean
}

export function ContractTypeSelect({ value, onChange, hideLabel = false }: ContractTypeSelectProps) {
  const { contractTypes } = useContractTypes()
  const canCreate = usePermission('contractType', 'create')
  const canUpdate = usePermission('contractType', 'update')
  const hasSelection = value.trim() !== ''
  const [sheetOpen, setSheetOpen] = useState(false)
  const [sheetDraft, setSheetDraft] = useState<ContractTypeSheetDraft | null>(null)

  function openCreate() {
    setSheetDraft({
      mode: 'create',
      name: '',
      value: '',
      guidelines: '',
    })
    setSheetOpen(true)
  }

  function openEditSelected() {
    const type = contractTypes.find(item => item.value === value)
    if (!type) return

    if (type.is_system) {
      setSheetDraft({
        mode: 'customize',
        name: type.name,
        value: type.value,
        guidelines: type.guidelines || '',
      })
    } else {
      setSheetDraft({
        mode: 'edit',
        id: type.id,
        name: type.name,
        value: type.value,
        guidelines: type.guidelines || '',
      })
    }
    setSheetOpen(true)
  }

  return (
    <div>
      {hideLabel ? null : (
        <label className="mb-2 block text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">
          Contract Type
        </label>
      )}
      <div className="flex gap-2">
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="min-w-0 flex-1">
            <SelectValue placeholder="Select contract type" />
          </SelectTrigger>
          <SelectContent>
            {contractTypes.map(type => (
              <SelectItem key={type.id} value={type.value}>{type.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canCreate ? (
          <IconButton
            type="button"
            size="lg"
            className="shrink-0"
            onClick={openCreate}
            title="Add contract type"
            aria-label="Add contract type"
          >
            <Plus className="h-4 w-4" />
          </IconButton>
        ) : null}
        {canUpdate && hasSelection ? (
          <IconButton
            type="button"
            size="lg"
            className="shrink-0"
            onClick={openEditSelected}
            title="Edit contract type"
            aria-label="Edit contract type"
          >
            <Pencil className="h-4 w-4" />
          </IconButton>
        ) : null}
      </div>

      <ContractTypeSheet
        open={sheetOpen}
        onOpenChange={v => {
          setSheetOpen(v)
          if (!v) setSheetDraft(null)
        }}
        draft={sheetDraft}
        onSaved={onChange}
      />
    </div>
  )
}
